import type { IncomingMessage } from 'http';

// Link previews (Open Graph / Twitter cards) are read by crawlers that don't run JS, so
// pages build these values in getServerSideProps and pass them as `pageProps.socialMeta`.

export type SocialMeta = {
  title: string;
  description: string;
  url: string;
  image: string;
  imageAlt: string;
};

export const SITE_NAME = 'IM3';
export const TWITTER_HANDLE = '@IM3_live';
export const DEFAULT_TITLE = 'IM3: video calls on a public network';
export const DEFAULT_DESCRIPTION =
  'Start a meeting in one click. Sign in with your wallet, or join as a guest with just a name.';

// Same target as the /api/v1 rewrite in next.config.js: im3Server as seen from this server.
export const API_TARGET = (
  process.env.API_PROXY_URL ||
  process.env.NEXT_PUBLIC_API_URL ||
  'http://localhost:3001'
).replace(/\/+$/, '');

// Crawlers need absolute URLs. SITE_URL wins when set; otherwise trust the proxy headers.
export const siteUrlFrom = (req: IncomingMessage) => {
  if (process.env.SITE_URL) return process.env.SITE_URL.replace(/\/+$/, '');
  const header = (name: string) =>
    String(req.headers[name] ?? '')
      .split(',')[0]
      .trim();
  const proto =
    header('x-forwarded-proto') ||
    ((req.socket as { encrypted?: boolean }).encrypted ? 'https' : 'http');
  const host = header('x-forwarded-host') || header('host') || 'localhost:8080';
  return `${proto}://${host}`;
};

export type UiConfig = { title?: string; desc?: string; logo?: string; privateRoom?: boolean };

// The default Meet/Space configs are what every unconfigured room falls back to.
export const isCustomConfig = (ui: UiConfig | null): boolean =>
  !!ui?.title && !/^IM3 (Meet|Space)$/.test(ui.title.trim());

export const fetchUiConfig = async (slug: string): Promise<UiConfig | null> => {
  if (!/^[A-Za-z0-9_-]+$/.test(slug)) return null;
  try {
    const res = await fetch(`${API_TARGET}/api/v1/rooms/getUiConfig/${slug}`, {
      signal: AbortSignal.timeout(1500),
    });
    return res.ok ? ((await res.json()) as UiConfig) : null;
  } catch {
    return null;
  }
};

export const roomSocialMeta = (slug: string, ui: UiConfig | null, siteUrl: string): SocialMeta => {
  const image = `${siteUrl}/api/og?room=${encodeURIComponent(slug)}`;
  const url = `${siteUrl}/rooms/${encodeURIComponent(slug)}`;
  if (ui && isCustomConfig(ui)) {
    return {
      title: `${ui.title} on IM3`,
      description: ui.desc?.trim() || DEFAULT_DESCRIPTION,
      url,
      image,
      imageAlt: `${ui.title} on IM3`,
    };
  }
  const product = ui?.title?.trim() || 'IM3 Meet';
  return {
    title: `Join ${slug} on ${product}`,
    description:
      'You’re invited to a video call. Join with your wallet, or as a guest with just a name.',
    url,
    image,
    imageAlt: `Invite to room ${slug} on ${product}`,
  };
};

export const defaultSocialMeta = (siteUrl = ''): SocialMeta => ({
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  url: siteUrl || '/',
  image: `${siteUrl}/api/og`,
  imageAlt: 'IM3: video calls on a public network',
});
